import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { ALL_USERS_TEAM_NAME } from '@common/constants';
import { IOption, UseFormReturn, convertDateToStringIgnoringTimezone } from '@gcl/react-core';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { IProjectUserProfile } from '@userProfile/projectUserProfile.models';
import { EProjectRequestStatus } from './projectRequest.constants';
import { EProjectRequestFormKeys, IProjectRequest, IUpsertProjectRequestRequest } from './projectRequest.models';
import { IProjectRequestCompany } from './projectRequestCompany/projectRequestCompany.models';
import { IProjectRequestTeam } from './projectRequestTeam/projectRequestTeam.models';
import { IProjectRequestUser, IUpsertProjectRequestUserRequest } from './projectRequestUser/projectRequestUser.models';

export const useProjectRequestDetailPage = (
  useFormMethods: UseFormReturn,
  riskCategoryOptions: IOption<IRiskCategory, string | number, 'key', string | number, 'text'>[] | undefined,
) => {
  const updateFormCompanyValueWithRes = (
    projectRequestCompanies?: IProjectRequestCompany[],
    projectRequestTeams?: IProjectRequestTeam[],
    projectRequestUsers?: IProjectRequestUser[],
  ) => {
    if (!projectRequestCompanies || !projectRequestUsers || !projectRequestTeams) return [];

    const companyFormData = projectRequestCompanies.map((company) => {
      const teamOfCompany = projectRequestTeams?.find((team) => team.projectRequestCompanyId === company.id);
      const mapRoles: { [key: string]: IProjectUserProfile[] } = {};
      projectRequestUsers
        .filter((user) => user.projectRequestTeamId === teamOfCompany?.id)
        .forEach((user) => {
          mapRoles[user.roleId] = [
            ...(mapRoles[user.roleId] ?? []),
            {
              id: user.id,
              userProfile: {
                ...user,
                id: user.userProfileId,
                email: user.email ?? '',
                uid: '',
                upn: '',
                displayName: user.displayName ?? '',
              },
              userProfileId: user.userProfileId,
            },
          ];
        });

      return {
        ...company,
        [EProjectRequestFormKeys.COMPANY_MAX_RISK_CATEGORY]:
          typeof company.maxRiskCategory === 'string'
            ? riskCategoryOptions?.find((option) => option.data?.category === company.maxRiskCategory)?.data ??
              company.maxRiskCategory
            : company.maxRiskCategory,
        [EProjectRequestFormKeys.ROLES]: mapRoles,
        isDefault: false,
      };
    });

    return companyFormData;
  };

  const getRequestObjFromFormValues = (
    data: any,
    mode: DetailPageAccessMode,
    projectRequestCompanies?: IProjectRequestCompany[],
    projectRequestTeams?: IProjectRequestTeam[],
    projectRequestUsers?: IProjectRequestUser[],
    projectRequestDetail?: IProjectRequest,
  ) => {
    const { projectRequest_Company: prCompanies, roles: prRoles = {}, ...generalInfo } = data;

    const companyData = prCompanies?.map((prCompany: any) => {
      const prRolesForCompany = prCompany[EProjectRequestFormKeys.ROLES] ?? {};
      const team = projectRequestTeams?.find((prTeam) => prTeam.projectRequestCompanyId === prCompany.id);

      let mergedUsers: Array<IUpsertProjectRequestUserRequest> = [];

      Object.keys(prRolesForCompany).map((roleId) => {
        const users = prRolesForCompany[roleId] ?? [];
        mergedUsers = [
          ...mergedUsers,
          ...users.map((user: IProjectUserProfile) => {
            const foundExistingUserRole = projectRequestUsers?.find(
              (existingUserRole) =>
                existingUserRole.roleId === Number(roleId) &&
                existingUserRole.userProfileId === (user.userProfileId ?? user?.userProfile?.id) &&
                existingUserRole.projectRequestTeamId === team?.id,
            );

            let userObj: { [key: string]: any } = {
              userProfileId: user.userProfileId ?? user.userProfile?.id,
              displayName: user.userProfile?.displayName,
              firstName: user.userProfile?.firstName,
              lastName: user.userProfile?.lastName,
              title: user.userProfile?.title,
              email: user.userProfile?.email,
              fax: user.userProfile?.fax,
              tel: user.userProfile?.tel,
              roleId: Number(roleId),
              processed: false,
              isExternal: false,
            };
            if (team) {
              userObj = {
                ...userObj,
                projectRequestTeamId: team.id,
              };
            }

            if (foundExistingUserRole) {
              userObj = {
                ...userObj,
                id: foundExistingUserRole.id,
                rowVersion: foundExistingUserRole.rowVersion,
              };
            }
            return userObj;
          }),
        ];
      });
      const teamItem: { [key: string]: any } = team
        ? { ...team, projectRequest_User: mergedUsers }
        : { teamName: ALL_USERS_TEAM_NAME, projectRequest_User: mergedUsers };

      let maxRiskCategoryResult = prCompany.maxRiskCategory;
      if (typeof prCompany.maxRiskCategory !== 'string') maxRiskCategoryResult = prCompany.maxRiskCategory.category;

      let companyItem = {
        code: (prCompany.code as string) ?? '',
        name: prCompany.name ?? '',
        companyTypeId: prCompany.companyTypeId,
        organizationTypeId: prCompany.organizationTypeId,
        maxRiskCategory: maxRiskCategoryResult,
        projectRequest_Team: [teamItem],
      };

      companyItem = { ...prCompany, ...companyItem };
      return companyItem;
    });
    const createRequest = {
      ...generalInfo,
      regionId: generalInfo?.regionId?.id,
      typeId: generalInfo?.typeId?.id,
      projectRequest_Company: companyData,
      status:
        generalInfo[EProjectRequestFormKeys.STATUS] ?? projectRequestDetail?.status ?? EProjectRequestStatus.DRAFT,
      startDate: convertDateToStringIgnoringTimezone(new Date(data.startDate)),
      finishDate: convertDateToStringIgnoringTimezone(new Date(data.finishDate)),
    };

    if (mode === 'Update' && projectRequestDetail?.id) {
      const updateRequest: IUpsertProjectRequestRequest = {
        ...projectRequestDetail,
        ...createRequest,
      };
      return updateRequest;
    }
    return createRequest;
  };

  return {
    updateFormCompanyValueWithRes,
    getRequestObjFromFormValues,
  };
};
